/**
 * Phone number — security settings card on the Profile page.
 *
 * Flow: type a number → a code is sent to it → type the code back → the number
 * is on the account, already proven. Changing a number later is the same path;
 * there is no way to simply edit the field (server-side, since migration 0076).
 *
 * The account keeps its current number until the new one is confirmed, so
 * closing this card half-way leaves the holder reachable where they were.
 *
 * This proves a contact channel. It is NOT a sign-in factor — that stays with
 * the authenticator app and passkeys above — and the copy must never suggest
 * otherwise.
 */
import { useCallback, useEffect, useState } from 'react';
import authService, { type PhoneVerificationStatus } from '../../services/auth.service';
import { getApiErrorCode, getApiErrorMessage } from '../../lib/apiError';
import { colors } from '../../styles/theme';
import {
  clock,
  copyForRefusal,
  mustAskForANewCode,
  needsAuthenticatorCode,
  sanitizeCode,
} from './phoneVerification.helpers';

type Step = 'idle' | 'number' | 'code';

/**
 * Enter submits the step. Done by hand because this card deliberately holds NO
 * <form>: it is also rendered inside the contract assistance modal, which is
 * itself a form, and a nested form is invalid HTML whose Enter key would submit
 * the OUTER one — sending the assistance request instead of the code.
 */
function onEnter(
  e: React.KeyboardEvent<HTMLInputElement>,
  allowed: boolean,
  action: () => Promise<void>,
): void {
  if (e.key !== 'Enter') return;
  e.preventDefault();
  if (allowed) void action();
}

function describe(err: unknown, fallback: string): string {
  return copyForRefusal(getApiErrorCode(err), getApiErrorMessage(err, fallback));
}

export default function PhoneVerificationSection({
  onVerified,
  embedded = false,
  initialPhone = '',
}: Readonly<{
  onVerified?: () => void;
  /** Prefill for the number step — the mobile already typed into an application. */
  initialPhone?: string;
  /** Rendered inside another flow that already explains why the number is
   *  needed (the contract assistance modal): no card chrome, no heading, and
   *  it opens straight on the number step instead of a status row. */
  embedded?: boolean;
}>): JSX.Element {
  const [status, setStatus] = useState<PhoneVerificationStatus | null>(null);
  const [step, setStep] = useState<Step>(embedded ? 'number' : 'idle');
  const [phone, setPhone] = useState(initialPhone);
  // Shown only once the server says this REPLACES a proven number.
  const [askAuthenticator, setAskAuthenticator] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setStatus(await authService.getPhoneVerification());
    } catch {
      // Silent: the card still renders and lets the holder start the flow.
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // One-second countdown while a code is live. Display only — the server owns
  // the expiry, and a code the clock here still shows as valid can be refused.
  useEffect(() => {
    if (step !== 'code' || secondsLeft <= 0) return undefined;
    const t = setTimeout(() => setSecondsLeft((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [step, secondsLeft]);

  async function sendCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const sent = await authService.requestPhoneCode(phone.trim(), mfaCode || undefined);
      setAskAuthenticator(false);
      setMfaCode('');
      setSentTo(sent.sent_to);
      setSecondsLeft(sent.expires_in_seconds);
      setCode('');
      setStep('code');
    } catch (err: unknown) {
      setError(describe(err, 'Could not send the code.'));
      if (needsAuthenticatorCode(getApiErrorCode(err))) {
        setAskAuthenticator(true);
        setMfaCode('');
      }
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await authService.confirmPhoneCode(code);
      setStep(embedded ? 'number' : 'idle');
      setPhone('');
      await load();
      onVerified?.();
    } catch (err: unknown) {
      setError(describe(err, 'Could not confirm the code.'));
      // A burned or expired code cannot be retried; go back to asking.
      if (mustAskForANewCode(getApiErrorCode(err))) setStep('number');
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    color: colors.ink,
  } as const;
  const primary = {
    background: colors.gold,
    color: colors.surface,
    opacity: busy ? 0.6 : 1,
  } as const;
  const hasNumber = Boolean(status?.phone_masked);
  const canSend = !busy && phone.trim().length >= 5 && (!askAuthenticator || mfaCode.length >= 6);
  const canConfirm = !busy && code.length === 6 && secondsLeft > 0;

  return (
    <section
      className={embedded ? '' : 'rounded-2xl p-6 max-w-2xl mt-6'}
      style={embedded ? undefined : { background: colors.surface, border: `1px solid ${colors.border}` }}
      aria-label="Phone number"
    >
      {!embedded && (
        <>
          <h2 className="text-base font-semibold" style={{ color: colors.ink }}>
            Phone number
          </h2>
          <p className="text-sm mt-1" style={{ color: colors.muted }}>
            The number we use to reach you. We confirm it with a one-time code, and ask again
            whenever you change it. It is not used to sign in.
          </p>
        </>
      )}

      {step === 'idle' && (
        <div className="mt-4 flex items-center justify-between gap-4">
          <div className="text-sm" style={{ color: colors.ink }}>
            {hasNumber ? status?.phone_masked : 'No number on file'}
            {hasNumber && (
              <span
                className="ml-2 text-xs px-2 py-0.5 rounded-full"
                style={{
                  background: status?.verified ? colors.successBg : colors.warningBg,
                  color: status?.verified ? colors.success : colors.warning,
                }}
              >
                {status?.verified ? 'Verified' : 'Not verified'}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setStep('number'); setError(null); }}
            className="text-sm font-medium px-3 py-1.5 rounded-lg"
            style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
          >
            {hasNumber ? 'Change number' : 'Add number'}
          </button>
        </div>
      )}

      {step === 'number' && (
        <div className="mt-4 space-y-3">
          <label className="block text-sm" style={{ color: colors.ink }}>
            <span>Mobile number, with country code</span>
            <input
              type="tel"
              autoComplete="tel"
              inputMode="tel"
              placeholder="+52 55 1234 5678"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => onEnter(e, canSend, sendCode)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm"
              style={inputStyle}
            />
          </label>
          {askAuthenticator && (
            <label className="block text-sm" style={{ color: colors.ink }}>
              <span>Authenticator app code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(sanitizeCode(e.target.value, 8))}
                onKeyDown={(e) => onEnter(e, canSend, sendCode)}
                className="mt-1 w-40 rounded-lg px-3 py-2 text-sm tracking-widest"
                style={inputStyle}
              />
            </label>
          )}
          <div className="flex gap-2">
            <button type="button" disabled={!canSend} onClick={() => { void sendCode(); }}
                    className="text-sm font-medium px-3 py-1.5 rounded-lg" style={primary}>
              {busy ? 'Sending…' : 'Send code'}
            </button>
            {!embedded && (
              <button type="button" onClick={() => { setStep('idle'); setError(null); }}
                      className="text-sm px-3 py-1.5 rounded-lg" style={{ color: colors.muted }}>
                Cancel
              </button>
            )}
          </div>
        </div>
      )}

      {step === 'code' && (
        <div className="mt-4 space-y-3">
          <p className="text-sm" style={{ color: colors.inkSoft }}>
            We sent a 6-digit code to <strong>{sentTo}</strong>.{' '}
            {secondsLeft > 0
              ? <>It expires in <span aria-live="off">{clock(secondsLeft)}</span>.</>
              : 'It has expired — ask for a new one.'}
          </p>
          <label className="block text-sm" style={{ color: colors.ink }}>
            <span>Code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              value={code}
              onChange={(e) => setCode(sanitizeCode(e.target.value))}
              onKeyDown={(e) => onEnter(e, canConfirm, confirm)}
              className="mt-1 w-40 rounded-lg px-3 py-2 text-sm tracking-widest"
              style={inputStyle}
            />
          </label>
          <div className="flex gap-2">
            <button type="button" disabled={!canConfirm} onClick={() => { void confirm(); }}
                    className="text-sm font-medium px-3 py-1.5 rounded-lg" style={primary}>
              {busy ? 'Checking…' : 'Confirm'}
            </button>
            <button type="button" onClick={() => { setStep('number'); setError(null); }}
                    className="text-sm px-3 py-1.5 rounded-lg" style={{ color: colors.muted }}>
              Use a different number
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg p-3 mt-3 text-sm" role="alert"
             style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
          {error}
        </div>
      )}
    </section>
  );
}
